import { useState, useEffect, useRef } from 'react';
import apiClient from '@/services/api';
import { AxiosError } from 'axios';

/**
 * LinkedIn Organization from search API
 */
export interface LinkedInOrganization {
  id: string;
  name: string;
  vanityName: string;
  urn: string;
  logo?: string | null;
  mentionText: string;
}

/**
 * Hook to search LinkedIn organizations with debouncing
 *
 * @param channelId - LinkedIn channel ID
 * @param query - Search query (minimum 3 characters)
 * @param debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns Search results, loading state, and error
 */
export const useLinkedInOrganizations = (
  channelId: number | null,
  query: string,
  debounceMs: number = 300
) => {
  const [organizations, setOrganizations] = useState<LinkedInOrganization[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use ref to track the latest query to avoid stale state in async operations
  const queryRef = useRef(query);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset state if query is too short or no channelId
    if (!channelId || query.length < 3) {
      setOrganizations([]);
      setIsSearching(false);
      setError(null);
      return;
    }

    // Set loading state immediately
    setIsSearching(true);
    setError(null);

    // Debounce the search
    const timeoutId = setTimeout(async () => {
      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const response = await apiClient.get(
          `/api/auth/linkedin/organizations/search`,
          {
            params: {
              q: query,
              channelId,
            },
            signal: abortController.signal,
          }
        );

        // Only update state if this is still the current query
        if (queryRef.current === query && !abortController.signal.aborted) {
          setOrganizations(response.data.data || []);
          setIsSearching(false);
        }
      } catch (err) {
        // Ignore aborted requests
        if ((err as Error).name === 'CanceledError' || (err as Error).name === 'AbortError') {
          return;
        }

        // Only update error if this is still the current query
        if (queryRef.current === query) {
          const axiosError = err as AxiosError<{ error?: string }>;
          const errorMessage =
            axiosError.response?.data?.error ||
            (err as Error).message ||
            'Failed to search organizations';

          setError(errorMessage);
          setOrganizations([]);
          setIsSearching(false);
        }
      }
    }, debounceMs);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [channelId, query, debounceMs]);

  return {
    organizations,
    isSearching,
    error,
  };
};
